"use client";

import { CreditCard } from "lucide-react";

import { useAppConfig } from "@/lib/config";
import { formatMoney, useBillingProfile } from "@/lib/money";

import { TopUpCreditsDialog } from "./top-up-credits-dialog";

import type { Organization } from "@/lib/types";

interface CreditsDisplayProps {
	organization: Organization | null;
	isLoading?: boolean;
}

export function CreditsDisplay({
	organization,
	isLoading,
}: CreditsDisplayProps) {
	const config = useAppConfig();
	const profile = useBillingProfile(organization?.id);

	if (isLoading) {
		return (
			<div className="px-2 py-1.5">
				<div className="w-full flex items-center justify-between p-2 rounded-md">
					<div className="flex items-center gap-2">
						<CreditCard className="h-4 w-4 text-muted-foreground" />
						<div className="flex flex-col">
							<span className="text-sm font-medium">Credits</span>
							<span className="text-xs text-muted-foreground">Loading...</span>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const creditsBalance = formatMoney(organization?.credits ?? 0, profile);

	// Show different styling based on credits balance
	const isLowCredits = organization && Number(organization.credits) < 1;
	const hasNoCredits = organization && Number(organization.credits) <= 0;

	// Enterprise: credits are admin-granted, so no self-serve top-up. Keep the
	// balance visible but drop the "Add"/top-up trigger.
	const managed = config.edition === "enterprise";

	const balanceInner = (
		<div className="flex items-center gap-2">
			<CreditCard
				className={`h-4 w-4 ${hasNoCredits ? "text-destructive" : isLowCredits ? "text-status-warn-icon" : "text-muted-foreground"}`}
			/>
			<div className="flex flex-col">
				<span className="text-sm font-medium">Credits</span>
				<span
					className={`text-xs ${hasNoCredits ? "text-destructive" : isLowCredits ? "text-status-warn-title" : "text-muted-foreground"}`}
				>
					{creditsBalance}
				</span>
			</div>
		</div>
	);

	return (
		<div className="px-2 py-1.5">
			{managed ? (
				<div className="w-full flex items-center justify-between p-2 rounded-md">
					{balanceInner}
				</div>
			) : (
				<TopUpCreditsDialog organization={organization}>
					<button className="w-full flex items-center justify-between p-2 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors text-left">
						{balanceInner}
						<span className="text-xs text-muted-foreground">Add</span>
					</button>
				</TopUpCreditsDialog>
			)}
			{hasNoCredits && (
				<div className="mt-1 px-2">
					<p className="text-xs text-destructive">⚠️ No credits remaining</p>
				</div>
			)}
			{isLowCredits && !hasNoCredits && (
				<div className="mt-1 px-2">
					<p className="text-xs text-status-warn-title">
						⚡ Low credits remaining
					</p>
				</div>
			)}
		</div>
	);
}
